import PageHeader from "../../components/ui/PageHeader";
import { Box, Button, Chip, FormControl, FormControlLabel, InputLabel, MenuItem, Paper, Select, Switch, TextField, Typography, useMediaQuery } from "@mui/material";
import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import Layout from "../../components/Layout";
import {
  getSettingDefinitions,
  getSettings,
  resetSetting,
  saveSetting,
  SettingArea,
  SettingValue,
  testSetting,
  checkStorage,
  StorageCheck,
  Values,
} from "../../service/PlatformSettingsService";
import { showSuccessToast } from "../../utils/Toast";

const orange = { "&:hover": { backgroundColor: "#D55A07" } };
const outlined = {}; // farver fra temaet

// Areas where a test sends something, and to what
const TEST_TARGET: Record<string, string> = { sms: "phone", smsFallback: "phone", email: "email" };
// Areas whose provider is a sub-processor (see Juridiske dokumenter → Underdatabehandlere)
const SUBPROCESSOR_AREAS = ["storage", "sms", "smsFallback", "email", "shortLinks", "payments", "marketing"];

/** One settings area: provider, fields and secrets (never shown - only whether they are set) */
function AreaForm({ area, value, onSaved }: { area: SettingArea; value: SettingValue; onSaved: (value: SettingValue) => void }) {
  const [t] = useTranslation("global");
  const isMobile = useMediaQuery("(max-width:600px)");
  const [provider, setProvider] = useState(value.provider);
  const [config, setConfig] = useState<Values>(value.config);
  const [secrets, setSecrets] = useState<Values>({});
  const [to, setTo] = useState("");
  const [result, setResult] = useState<{ ok: boolean; message: string } | null>(null);
  const [busy, setBusy] = useState(false);
  const [missing, setMissing] = useState<NonNullable<StorageCheck["missing"]>>([]);

  const providerDefinition = area.providers.find((item) => item.key === provider);
  const fields = providerDefinition?.fields || [];
  const sameProvider = provider === value.provider;

  const changeProvider = (next: string) => {
    setProvider(next);
    setSecrets({});
    setResult(null);
    const defaults: Values = {};
    (area.providers.find((item) => item.key === next)?.fields || []).forEach((field) => {
      if (!field.secret && field.default !== undefined) defaults[field.key] = field.default;
    });
    setConfig(next === value.provider ? value.config : defaults);
  };

  const save = async () => {
    setBusy(true);
    const saved = await saveSetting(area.key, provider, config, secrets, isMobile);
    if (saved) {
      showSuccessToast(t("platformSettings.saved"), isMobile);
      setSecrets({});
      // A new provider of these is a new sub-processor: the list must be updated and the customers told in advance
      if (SUBPROCESSOR_AREAS.includes(area.key) && saved.provider !== value.provider) {
        setResult({ ok: false, message: t("platformSettings.subprocessorHint") });
      }
      onSaved(saved);
    }
    setBusy(false);
  };

  const reset = async () => {
    setBusy(true);
    const saved = await resetSetting(area.key, isMobile);
    if (saved) {
      setProvider(saved.provider);
      setConfig(saved.config);
      setSecrets({});
      onSaved(saved);
    }
    setBusy(false);
  };

  const test = async () => {
    setBusy(true);
    setResult(null);
    setMissing([]);
    setResult(await testSetting(area.key, provider, config, secrets, to));
    setBusy(false);
  };

  /** Storage: are all breef videos in the storage in the form? Before a switch (copy complete?) and after. */
  const check = async () => {
    setBusy(true);
    setResult(null);
    setMissing([]);
    const checked = await checkStorage(provider, config, secrets);
    if (checked.total === undefined) {
      setResult({ ok: false, message: checked.message || "" });
      setBusy(false);
      return;
    }
    const parts = [t("platformSettings.check.found", { found: checked.found, total: checked.total })];
    if (checked.missingCount) parts.push(t("platformSettings.check.missing", { count: checked.missingCount }));
    if (checked.external) parts.push(t("platformSettings.check.external", { count: checked.external }));
    if (checked.notConverted) parts.push(t("platformSettings.check.notConverted", { count: checked.notConverted }));
    if (checked.publiclyReadable !== null && checked.publiclyReadable !== undefined) {
      parts.push(
        checked.publiclyReadable
          ? t("platformSettings.check.publicOk")
          : t("platformSettings.check.publicFailed", { status: checked.publicStatus })
      );
    }
    setMissing(checked.missing || []);
    setResult({ ok: checked.ok, message: parts.join(". ") });
    setBusy(false);
  };

  return (
    <Paper sx={{ width: "100%", mt: 2, padding: "16px 24px" }}>
      <Box sx={{ display: "flex", alignItems: "center", gap: 2, flexWrap: "wrap" }}>
        <Typography variant="h6">{area.label}</Typography>
        <Chip size="small" label={value.saved ? t("platformSettings.fromAdmin") : t("platformSettings.fromEnv")} />
      </Box>
      <Typography variant="body2" color="text.secondary" sx={{ marginBottom: 2 }}>
        {area.description}
      </Typography>
      <Box sx={{ display: "flex", flexWrap: "wrap", gap: 2, alignItems: "center" }}>
        <FormControl size="small" sx={{ minWidth: 220 }}>
          <InputLabel>{t("platformSettings.provider")}</InputLabel>
          <Select label={t("platformSettings.provider")} value={provider} onChange={(event) => changeProvider(String(event.target.value))}>
            {area.providers.map((item) => (
              <MenuItem key={item.key} value={item.key}>
                {item.label}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        {fields.map((field) =>
          field.type === "boolean" ? (
            <FormControlLabel
              key={field.key}
              label={field.label}
              control={<Switch color="warning" checked={Boolean(config[field.key])} onChange={(event) => setConfig({ ...config, [field.key]: event.target.checked })} />}
            />
          ) : field.secret ? (
            <TextField
              key={field.key}
              size="small"
              type="password"
              autoComplete="new-password"
              label={`${field.label}${field.required ? " *" : ""}`}
              value={String(secrets[field.key] ?? "")}
              placeholder={sameProvider && value.secrets[field.key] ? t("platformSettings.secretSet") : ""}
              InputLabelProps={{ shrink: true }}
              helperText={sameProvider && value.secrets[field.key] ? t("platformSettings.secretSetHelp") : field.help}
              onChange={(event) => setSecrets({ ...secrets, [field.key]: event.target.value })}
              sx={{ minWidth: 260 }}
            />
          ) : (
            <TextField
              key={field.key}
              size="small"
              type={field.type === "number" ? "number" : "text"}
              label={`${field.label}${field.required ? " *" : ""}`}
              value={String(config[field.key] ?? "")}
              helperText={field.help}
              onChange={(event) => setConfig({ ...config, [field.key]: event.target.value })}
              sx={{ minWidth: 260 }}
            />
          )
        )}
      </Box>
      <Box sx={{ display: "flex", flexWrap: "wrap", gap: 2, alignItems: "center", marginTop: 2 }}>
        <Button variant="contained" onClick={save} disabled={busy} sx={orange}>
          {t("platformSettings.save")}
        </Button>
        {TEST_TARGET[area.key] && provider !== "none" && (
          <TextField
            size="small"
            label={t(`platformSettings.testTo.${TEST_TARGET[area.key]}`)}
            value={to}
            onChange={(event) => setTo(event.target.value)}
          />
        )}
        {area.key !== "support" && (
          <Button variant="outlined" onClick={test} disabled={busy} sx={outlined}>
            {t("platformSettings.test")}
          </Button>
        )}
        {area.key === "storage" && (
          <Button variant="outlined" onClick={check} disabled={busy} sx={outlined}>
            {t("platformSettings.check.button")}
          </Button>
        )}
        {value.saved && (
          <Button variant="outlined" onClick={reset} disabled={busy} sx={outlined}>
            {t("platformSettings.reset")}
          </Button>
        )}
        {result && (
          <Typography variant="body2" color={result.ok ? "success.main" : "error.main"}>
            {result.message}
          </Typography>
        )}
      </Box>
      {missing.length > 0 && (
        <Box sx={{ marginTop: 1 }}>
          {missing.map((file) => (
            <Typography key={file.key} variant="body2" color="error.main">
              {file.key} ({file.breefName}{file.deleted ? `, ${t("platformSettings.check.deleted")}` : ""})
            </Typography>
          ))}
        </Box>
      )}
    </Paper>
  );
}

/** Platform settings: the provider for each external service (superadmin) */
export default function PlatformSettings() {
  const [t] = useTranslation("global");
  const [areas, setAreas] = useState<SettingArea[]>([]);
  const [values, setValues] = useState<SettingValue[]>([]);

  useEffect(() => {
    Promise.all([getSettingDefinitions(), getSettings()])
      .then(([definitions, current]) => {
        setAreas(definitions);
        setValues(current);
      })
      .catch(() => undefined);
  }, []);

  return (
    <Layout>
      <PageHeader title={t("platformSettings.title")} description={t("platformSettings.intro")} />
      {areas.map((area) => {
        const value = values.find((item) => item.area === area.key);
        return value ? (
          <AreaForm
            key={`${area.key}-${value.provider}-${value.saved}`}
            area={area}
            value={value}
            onSaved={(saved) => setValues(values.map((item) => (item.area === saved.area ? saved : item)))}
          />
        ) : null;
      })}
      <Box sx={{ height: 40 }} />
    </Layout>
  );
}
